import { Button } from 'primereact/button';
import { AutoComplete } from 'primereact/autocomplete';
import { MultiSelect } from 'primereact/multiselect';
import { Chip } from 'primereact/chip';
import { Controller } from 'react-hook-form';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import InputErrorMessage from 'components/UI/InputErrorMessage/InputErrorMessage';
import LabelName from 'components/UI/LabelName/LabelName';
import { useUserDetailsContext } from 'context/userDetailsContext';
import CaseListDropDown from 'components/CaseListDropDown/CaseListDropDown';
import { Chips } from 'primereact/chips';
import { sanitizeHtmlColorContent } from 'utils/utils';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { useEffect, useRef, useState } from 'react';
import { useMailboxRecipientContacts } from '../hooks/useMailboxRecipientContacts';
import Dropzone from 'react-dropzone';

const MailCreateForm = ({
  show,
  caseId,
  onSubmit,
  handleSubmit,
  handleClose,
  control,
  errors,
  watch,
  trigger,
  isLoading,
  existingFiles = [],
  pendingUploadFiles = [],
  setPendingUploadFiles,
}) => {
  const { userDetails } = useUserDetailsContext();
  const { addToast } = useToast();
  const maxFiles = 20;
  /** Wrapper ref for the To field (compose recipients); keeps any legacy `chipsRef` references valid. */
  const chipsRef = useRef(null);
  const chipsRefCc = useRef(null);
  const chipsRefBcc = useRef(null);
  const [toInput, setToInput] = useState('');
  const { suggestions: toContactSuggestions, searchContacts: searchToContacts } = useMailboxRecipientContacts();

  useEffect(() => {
    if (!show) setToInput('');
  }, [show]);

  // Helper function to check for duplicate emails
  const checkForDuplicates = (emails, fieldName) => {
    if (!emails || emails.length === 0) return null;

    const trimmedEmails = emails.map((email) => email.trim().toLowerCase());
    const uniqueEmails = new Set(trimmedEmails);

    // Check for duplicates within the same field
    if (trimmedEmails.length !== uniqueEmails.size) {
      const duplicates = trimmedEmails.filter((email, index) => trimmedEmails.indexOf(email) !== index);
      return `Duplicate email found in ${fieldName}: ${duplicates[0]}`;
    }

    return null;
  };

  // Helper function to check for duplicates across fields
  const checkAcrossFields = (currentEmails, currentFieldName, otherFields) => {
    if (!currentEmails || currentEmails.length === 0) return null;

    const currentTrimmed = currentEmails.map((email) => email.trim().toLowerCase());

    for (const [fieldName, fieldValue] of Object.entries(otherFields)) {
      if (fieldValue && Array.isArray(fieldValue)) {
        const otherTrimmed = fieldValue.map((email) => email.trim().toLowerCase());

        for (const email of currentTrimmed) {
          if (otherTrimmed.includes(email)) {
            return `Email ${email} is already added in ${fieldName}`;
          }
        }
      }
    }

    return null;
  };

  const handleOnBlur = (e, field, reference) => {
    const inputEl = reference.current?.element?.querySelector('input');
    const inputValue = inputEl?.value?.trim();

    if (inputValue) {
      const alreadyExists = field.value?.includes(inputValue);
      if (!alreadyExists) {
        field.onChange([...(field.value || []), inputValue]);
      }
      inputEl.value = '';
    }
  };

  const appendRecipientEmail = (emailRaw, field) => {
    const trimmed = (emailRaw || '').trim();
    if (!trimmed) return;
    const lower = trimmed.toLowerCase();
    const exists = field.value?.some((e) => e.trim().toLowerCase() === lower);
    if (exists) return;
    field.onChange([...(field.value || []), trimmed]);
    trigger('recipient_list');
  };

  const removeRecipientEmail = (email, field) => {
    field.onChange((field.value || []).filter((e) => e !== email));
    trigger('recipient_list');
  };

  /** Commits the current To input text as a recipient (Enter, comma, or blur). */
  const commitManualToInput = (field) => {
    setToInput((current) => {
      const v = typeof current === 'string' ? current.trim() : '';
      if (v) appendRecipientEmail(v, field);
      return '';
    });
  };

  return (
    <div className="h-100">
      <form className="F-size14 message-create-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="justify-content-between align-items-center mb-3 mt-3">
          <div className="modal-heading F-size16 text-bold">Compose Mail</div>
          <div className="d-flex align-items-center py-2">
            <div className="col-12">
              <div>
                <div className="d-flex align-items-center py-2 flex-wrap ">
                  {/* To */}
                  <div className="col-md-4 col-12">
                    <LabelName required={true} htmlFor="recipient_list">
                      To
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="recipient_list"
                      control={control}
                      rules={{
                        required: 'Required',
                        validate: (value) => {
                          // Check for duplicates within the To field
                          const duplicateCheck = checkForDuplicates(value, 'To');
                          if (duplicateCheck) return duplicateCheck;

                          // Check for duplicates across CC and BCC fields
                          const ccList = watch('cc_list');
                          const bccList = watch('bcc_list');
                          const crossFieldCheck = checkAcrossFields(value, 'To', {
                            Cc: ccList,
                            Bcc: bccList,
                          });
                          if (crossFieldCheck) return crossFieldCheck;

                          // Check if the recipient list contains the user's email
                          if (value.some((email) => email.trim() === userDetails?.business_email)) {
                            return 'You cannot send an email to your own address';
                          }

                          // Check if the email domain ends with .lawft.com
                          const internalDomainRegex = /@.*\.lawft\.com$/i;

                          // Validate each email format using regex
                          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                          for (const email of value) {
                            const trimmedEmail = email.trim();

                            // Check for internal user email domain
                            if (internalDomainRegex.test(trimmedEmail)) {
                              return 'You cannot send email to internal users';
                            }

                            if (!emailRegex.test(email.trim())) {
                              return `Invalid email format: ${email}`;
                            }
                          }

                          return true; // No error if all validations pass
                        },
                      }}
                      render={({ field }) => (
                        <div
                          ref={chipsRef}
                          className="w-100 input-shadow ps-2 py-1 rounded compose-to-recipients d-flex flex-wrap align-items-center gap-1"
                          id={field.name}
                        >
                          {(field.value || []).map((email) => (
                            <Chip
                              key={email}
                              label={email}
                              removable
                              className="p-mb-1"
                              onRemove={() => removeRecipientEmail(email, field)}
                            />
                          ))}
                          <AutoComplete
                            inputRef={field.ref}
                            className="flex-grow-1 compose-to-autocomplete"
                            inputClassName="border-0 shadow-none"
                            style={{ minWidth: '160px' }}
                            placeholder={
                              field.value?.length ? 'Add another — search contacts or type email' : 'Search contacts by name or type email'
                            }
                            value={toInput}
                            suggestions={toContactSuggestions}
                            completeMethod={searchToContacts}
                            field="label"
                            onChange={(e) => {
                              const v = e.value;
                              if (v && typeof v === 'object' && v.email) {
                                appendRecipientEmail(v.email, field);
                                setToInput('');
                                return;
                              }
                              setToInput(typeof v === 'string' ? v : '');
                            }}
                            onSelect={(e) => {
                              const em = e.value?.email;
                              if (em) {
                                appendRecipientEmail(em, field);
                                setToInput('');
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ',') {
                                e.preventDefault();
                                commitManualToInput(field);
                              }
                            }}
                            onBlur={() => {
                              window.setTimeout(() => {
                                commitManualToInput(field);
                              }, 200);
                            }}
                            autoComplete="off"
                          />
                        </div>
                      )}
                    />
                    {errors.recipient_list && <InputErrorMessage>{errors.recipient_list.message}</InputErrorMessage>}
                  </div>
                </div>
                <div className="d-flex align-items-center py-2 flex-wrap ">
                  {/* cc */}
                  <div className="col-md-4 col-12">
                    <LabelName htmlFor="cc_list">Cc</LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="cc_list"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (value) {
                            // Check for duplicates within the CC field
                            const duplicateCheck = checkForDuplicates(value, 'Cc');
                            if (duplicateCheck) return duplicateCheck;

                            // Check for duplicates across To and BCC fields
                            const toList = watch('recipient_list');
                            const bccList = watch('bcc_list');
                            const crossFieldCheck = checkAcrossFields(value, 'Cc', {
                              To: toList,
                              Bcc: bccList,
                            });
                            if (crossFieldCheck) return crossFieldCheck;

                            // Check if the recipient list contains the user's email
                            if (value.some((email) => email.trim() === userDetails?.business_email)) {
                              return 'You cannot send an email to your own address';
                            }

                            // Check if the email domain ends with .lawft.com
                            const internalDomainRegex = /@.*\.lawft\.com$/i;
                            // Validate each email format using regex
                            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                            for (const email of value) {
                              const trimmedEmail = email.trim();

                              // Check for internal user email domain
                              if (internalDomainRegex.test(trimmedEmail)) {
                                return 'You cannot send email to internal users';
                              }

                              if (!emailRegex.test(email.trim())) {
                                return `Invalid email format: ${email}`;
                              }
                            }

                            return true; // No error if all validations pass
                          }
                        },
                      }}
                      render={({ field }) => (
                        <Chips
                          inputRef={field.ref}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          placeholder={field.value ? '' : 'cc'}
                          className={'w-100 input-shadow ps-2'}
                          separator=","
                          ref={chipsRefCc}
                          onBlur={(e) => handleOnBlur(e, field, chipsRefCc)}
                        />
                      )}
                    />
                    {errors.cc_list && <InputErrorMessage>{errors.cc_list.message}</InputErrorMessage>}
                  </div>
                </div>
                <div className="d-flex align-items-center py-2 flex-wrap ">
                  {/* bcc */}
                  <div className="col-md-4 col-12">
                    <LabelName htmlFor="bcc_list">Bcc</LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="bcc_list"
                      control={control}
                      rules={{
                        validate: (value) => {
                          if (value) {
                            // Check for duplicates within the BCC field
                            const duplicateCheck = checkForDuplicates(value, 'Bcc');
                            if (duplicateCheck) return duplicateCheck;

                            // Check for duplicates across To and CC fields
                            const toList = watch('recipient_list');
                            const ccList = watch('cc_list');
                            const crossFieldCheck = checkAcrossFields(value, 'Bcc', {
                              To: toList,
                              Cc: ccList,
                            });
                            if (crossFieldCheck) return crossFieldCheck;

                            // Check if the recipient list contains the user's email
                            if (value.some((email) => email.trim() === userDetails?.business_email)) {
                              return 'You cannot send an email to your own address';
                            }

                            // Check if the email domain ends with .lawft.com
                            const internalDomainRegex = /@.*\.lawft\.com$/i;
                            // Validate each email format using regex
                            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
                            for (const email of value) {
                              const trimmedEmail = email.trim();

                              // Check for internal user email domain
                              if (internalDomainRegex.test(trimmedEmail)) {
                                return 'You cannot send email to internal users';
                              }

                              if (!emailRegex.test(email.trim())) {
                                return `Invalid email format: ${email}`;
                              }
                            }

                            return true; // No error if all validations pass
                          }
                        },
                      }}
                      render={({ field }) => (
                        <Chips
                          inputRef={field.ref}
                          id={field.name}
                          value={field.value}
                          onChange={(e) => field.onChange(e.value)}
                          placeholder={field.value ? '' : 'bcc'}
                          className={'w-100 input-shadow ps-2'}
                          separator=","
                          ref={chipsRefBcc}
                          onBlur={(e) => handleOnBlur(e, field, chipsRefBcc)}
                        />
                      )}
                    />
                    {errors.bcc_list && <InputErrorMessage>{errors.bcc_list.message}</InputErrorMessage>}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName required={true} htmlFor="subject">
                    Subject
                  </LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    name="subject"
                    control={control}
                    rules={{ required: 'Required' }}
                    defaultValue=""
                    render={({ field }) => (
                      <TextSnippetWrapper
                        type="text"
                        id={field.name}
                        {...field}
                        placeholder="Subject"
                        className={'w-100 input-shadow'}
                        autoComplete="off"
                      />
                    )}
                  />
                  {errors.subject && <InputErrorMessage>Required</InputErrorMessage>}
                </div>
              </div>
              {!caseId && (
                <div className="d-flex align-items-center py-2 flex-wrap">
                  <div className="col-md-4 col-12">
                    <LabelName required={true} htmlFor="subject">
                      Case
                    </LabelName>
                  </div>
                  <div className="col-md-8 col-12">
                    <Controller
                      name="case"
                      control={control}
                      defaultValue={null}
                      rules={{ required: 'Case is required' }} // Adding validation rule
                      render={({ field }) => (
                        <CaseListDropDown {...field} placeholder="Case/Lead Name" noOptionsMessage="No Cases/Leads Found" />
                      )}
                    />
                    {/* Display error for Case field */}
                    {errors.case && <InputErrorMessage>{errors?.case?.message}</InputErrorMessage>}
                  </div>
                </div>
              )}
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12">
                  <LabelName htmlFor="html_body">Body</LabelName>
                </div>
                <div className="col-md-8 col-12">
                  <Controller
                    control={control}
                    name="html_body"
                    className="shadow-small"
                    render={({ field: { onChange, value } }) => (
                      <TextSnippetWrapper
                        className="email-quill-editor"
                        type="editor"
                        value={sanitizeHtmlColorContent(value)}
                        onChange={(e) => onChange(e)}
                      />
                    )}
                  />
                  {errors?.html_body && <InputErrorMessage>Please enter your message</InputErrorMessage>}
                </div>
              </div>
              {/* Attachments: new uploads + existing case files (same pattern as thread reply) */}
              <div className="d-flex align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 mt-2">
                  <LabelName required={false} htmlFor="attachments">
                    Attachments
                  </LabelName>
                </div>
                <div className="col-md-8 col-12 mt-2">
                  <div className="d-flex flex-column gap-4">
                    {pendingUploadFiles.length > 0 && (
                      <ul className="list-unstyled">
                        {pendingUploadFiles.map((file, index) => (
                          <div key={`${file.name}-${index}`} className="d-flex align-items-center">
                            <i className="fas fa-paperclip me-2 mt-1" aria-hidden="true" />
                            <li className="text-break ms-1">{file.name}</li>
                            <button
                              type="button"
                              className="icon-delete ms-3 pointer border-0 bg-transparent p-0"
                              aria-label={`Remove ${file.name}`}
                              onClick={() => setPendingUploadFiles((prev) => prev.filter((_, i) => i !== index))}
                            />
                          </div>
                        ))}
                      </ul>
                    )}
                    <div className="col-12 px-2 drag-drop-box compose-mail-attachments-row">
                      <div className="compose-mail-attachment-upload">
                        <Dropzone
                          onDrop={(acceptedFiles) => {
                            const existingSelected = (watch('attachments') || []).filter((a) => a && typeof a === 'object' && a.file_path);
                            const next = [...pendingUploadFiles, ...acceptedFiles];
                            if (next.length + existingSelected.length > maxFiles) {
                              addToast(
                                false,
                                toastConstant.toasterType.WARN,
                                toastConstant.header.WARNINIG,
                                toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
                              );
                              return;
                            }
                            setPendingUploadFiles(next);
                          }}
                          accept="application/pdf, application/msword, .docx, .doc, audio/*, video/*, image/*, .heic, .heif, image/heic, image/heif"
                        >
                          {({ getRootProps, getInputProps }) => (
                            <section className="d-flex align-items-center justify-content-center w-100">
                              <div
                                {...getRootProps({
                                  className: 'compose-mail-attachment-drop-target',
                                })}
                              >
                                <input {...getInputProps()} />
                                <p className="text-center mb-0">Click or drag file to this area to upload</p>
                              </div>
                            </section>
                          )}
                        </Dropzone>
                      </div>
                      <p className="mb-0 d-flex align-items-center compose-mail-attachment-or">
                        <span>or</span>
                      </p>
                      <div className="compose-mail-attachment-existing">
                        <Controller
                          name="attachments"
                          control={control}
                          defaultValue={[]}
                          render={({ field: { name, value, onChange } }) => (
                            <MultiSelect
                              options={existingFiles?.length ? existingFiles : [{ label: 'No Records Found', value: 'no-records-found' }]}
                              className="input-shadow existing-files"
                              placeholder="Select or Search Existing Files"
                              display="chip"
                              value={value || []}
                              filter
                              onChange={(e) => {
                                const next = e.value || [];
                                const count = next.filter((v) => v && typeof v === 'object' && v.file_path).length;
                                if (count + pendingUploadFiles.length > maxFiles) {
                                  addToast(
                                    false,
                                    toastConstant.toasterType.WARN,
                                    toastConstant.header.WARNINIG,
                                    toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
                                  );
                                  return;
                                }
                                onChange(next);
                              }}
                              optionLabel="label"
                              optionValue="value"
                              optionDisabled={(item) => item?.value === 'no-records-found'}
                              showClear
                              resetFilterOnHide
                              id={name}
                              emptyFilterMessage="No records found"
                              showSelectAll={false}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="footer-btn">
                <Button type="button" label="Cancel" className="p-button p-button-secondary outline me-2" onClick={handleClose} />
                <Button type="submit" label="Send" className="p-button p-button-secondary" disabled={isLoading} />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default MailCreateForm;
